//
// query/normalize.ts
//
// Copyright 2025-Present Couchbase, Inc.
//
// Use of this software is governed by the Business Source License included
// in the file licenses/BSL-Couchbase.txt.  As of the Change Date specified
// in that file, in accordance with the Business Source License, use of this
// software will be governed by the Apache License, Version 2.0, included in
// the file licenses/APL2.txt.
//

import type { Select, Expr, Operation} from "./schema";
import { ExprIsLiteral } from "./schema";


/* Functions for processing and scanning JSON queries. */


/** Converts a JSON `Select` object and its contents into normal form, in-place.
 *  - Uppercases all object keys and operator names.
 *  - Turns result columns declared as plain path strings into proper '.' operations.
 *  - Breaks up property paths, for example `[".foo.bar"]` becomes `[".", "foo", "bar"]`.
 *  - Converts shorthand `["$foo"]` into `["$", "foo"]`, and the same for '?'.
 * This isn't necessary for JSON generated by this library's N1QL parser,
 * but is required if we decide to accept externally-created JSON, whether hand-written or
 * produced by LiteCore. */
export function Normalize(select: Select) {
    // Uppercase keys of the SELECT itself:
    UppercaseKeys(select as unknown as Record<string,unknown>);
    for (let from of select.FROM)
        UppercaseKeys(from as unknown as Record<string,unknown>);

    // Fix result columns declared as plain path strings:
    select.WHAT = select.WHAT.map( col => {
        if (typeof col === 'string')
            return ['.' + col];
        else
            return col;
    });

    // Uppercase ops and split paths:
    VisitSelect(select, (op, expr) => {
        const first = op[0];
        if ((first === '.' || first === '$' || first === '?') && op.length > 1) {
            // `[".foo.bar"]` -> `[".", "foo", "bar"]`, etc.
            expr.splice(1, 0, ... op.substring(1).split('.'));
            expr[0] = op[0];
        } else {
            expr[0] = op.toUpperCase();
        }
        return true;
    });
}


/** Ensures that every '.' and 'META()' expression starts with a valid source alias. */
export function NormalizePaths(select: Select,
                               aliases: Map<string,unknown>,
                               singleSource: string | undefined)
{
    VisitSelect(select, (op, expr) => {
        if (op === '.') {
            // Path must start with an alias:
            const alias = expr[1] as string | undefined;
            if (alias === undefined || (!aliases.has(alias))) {
                if (singleSource === undefined)
                    // eslint-disable-next-line @typescript-eslint/no-base-to-string
                    throw Error(`property path ${expr.slice(1).join('.')} does not start with an alias`);
                expr.splice(1, 0, singleSource);
            }
        } else if (op === 'META()') {
            if (expr.length < 2 || expr[1] === null) {
                if (singleSource === undefined)
                    throw Error("ambiguous META() needs a collection name as argument");
                expr[1] = singleSource;
            }
        }
        return true;
    });
}


/** Converts all the keys of an object to uppercase, in place. */
function UppercaseKeys(obj: Record<string,unknown>) {
    for (const key of Object.getOwnPropertyNames(obj)) {
        const upperKey = key.toUpperCase();
        if (upperKey !== key) {
            if (upperKey in obj) throw Error(`Conflicting keys "${key}" and "${upperKey}"`);
            obj[upperKey] = obj[key];
            delete obj[key];
        }
    }
}


/** Calls `visitor` on every Operation. Iteration stops if it returns false. */
export function VisitSelect(select: Select, visitor: (op: string, subexpr: Operation)=>boolean) {
    function visit(expr: Expr | undefined) {
        if (expr !== undefined) VisitExpr(expr, visitor);
    }

    for (let what of select.WHAT)
        visit(what);
    for (let from of select.FROM) {
        if ('ON' in from)
            visit(from.ON);
        if ('UNNEST' in from)
            visit(from.UNNEST);
    }
    visit(select.WHERE);
    visit(select.HAVING);
    if (select.GROUP_BY)
        for (let expr of select.GROUP_BY) visit(expr);
    if (select.ORDER_BY)
        for (let expr of select.ORDER_BY) visit(expr);
    visit(select.LIMIT);
    visit(select.OFFSET);
}


/** Calls `visitor` on every Operation. Iteration stops if it returns false. */
export function VisitExpr(expr: Expr, visitor: (op: string, subexpr: Operation)=>boolean): boolean {
    return visit(expr);

    function visit(subExpr: Expr): boolean {
        if (ExprIsLiteral(subExpr)) {
            return true;
        } else if (Array.isArray(subExpr)) {
            const op = subExpr[0];
            if (!visitor(op, subExpr))
                return false;
            if (op !== '.' && op !== 'META()') {
                const len = subExpr.length;
                for (let i = 1; i < len; ++i) {
                    if (!visit(subExpr[i]))
                        return false;
                }
            }
            return true;
        } else {
            for (const key of Object.getOwnPropertyNames(subExpr)) {
                if (!visit(subExpr[key]))
                    return false;
            }
            return true;
        }
    }
}


/** Returns the set of source/result aliases accessed by an Expr. */
export function ExprAliases(expr: Expr): Set<string> {
    let aliases = new Set<string>();
    VisitExpr(expr, (op, subexpr) => {
        if (op === '.' || op === 'META()')
            aliases.add(subexpr[1] as string);
        return true;
    });
    return aliases;
}


/** Given a predicate expression such as a SELECT's WHERE or a JOIN's ON,
 *  converts it to an array of one or more Operations that must all be true,
 *  i.e. that are implicitly joined by AND. */
export function SplitANDs(where: Expr): Operation[] {
    let conditions: Operation[] = [];
    const process = (expr: Expr) => {
        if (!Array.isArray(expr)) {
            // A truthy literal has no effect; a falsy literal means no results possible.
            if (!expr)
                throw Error("WHERE or ON condition is always false; no results possible");
        } else if (expr[0] === 'AND') {
            // Recurse on the sub-expressions of the AND:
            for (let i = 1; i < expr.length; ++i) {
                process(expr[i]);
            }
        } else {
            // Other operations become individual terams:
            conditions.push(expr);
        }
    };
    process(where);
    return conditions;
}
